import { Button } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { SchedulePublicBookingLink } from "@/api/scheduling/schedule-public.types";
import { colors } from "@/design-system/theme/colors";
import {
  getInitialBookingDate,
  getPublicBookingWindowEndDate,
  isDateAvailable,
} from "../utils";

interface BookingCalendarProps {
  bookingLink?: SchedulePublicBookingLink;
  selectedDate: Dayjs;
  visibleMonth: Dayjs;
  /**
   * Set of backend-confirmed available dates (YYYY-MM-DD). Once loaded, this is
   * the source of truth for selectable dates.
   */
  availableDates: ReadonlySet<string> | null;
  isDatesLoading?: boolean;
  /** Uses smaller day tiles for space-constrained internal booking flows. */
  compact?: boolean;
  onMonthChange: (month: Dayjs) => void;
  onDateSelect: (date: Dayjs) => void;
}

const WEEKDAYS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

// Gray highlight used for selectable/available dates (per design spec).
const AVAILABLE_DATE_BG = colors.surfaceSubtle;

const buildCalendarDays = (month: Dayjs): Dayjs[] => {
  const start = month.startOf("month").startOf("week");
  return Array.from({ length: 42 }, (_, index) => start.add(index, "day"));
};

export const BookingCalendar = ({
  bookingLink,
  selectedDate,
  visibleMonth,
  availableDates,
  isDatesLoading = false,
  compact = false,
  onMonthChange,
  onDateSelect,
}: BookingCalendarProps) => {
  const days = buildCalendarDays(visibleMonth);
  const minimumBookingMonth = getInitialBookingDate(bookingLink).startOf("month");
  const maximumBookingMonth =
    getPublicBookingWindowEndDate(bookingLink)?.startOf("month") ?? null;
  const canGoPreviousMonth = visibleMonth
    .startOf("month")
    .isAfter(minimumBookingMonth);
  const canGoNextMonth =
    !maximumBookingMonth ||
    visibleMonth.startOf("month").isBefore(maximumBookingMonth);

  // Selected-date accent uses the workspace brand color when provided.
  const brandColor =
    bookingLink?.workspace?.brandColor?.trim() || colors.primary;
  const isHex6Color = /^#[0-9a-f]{6}$/i.test(brandColor);
  const selectedShadow = isHex6Color
    ? `0 8px 18px ${brandColor}2E`
    : "0 8px 18px rgba(0, 0, 0, 0.15)";

  return (
    <section
      className={
        compact
          ? "flex min-h-[360px] flex-col border-b px-4 py-4 sm:min-h-[390px] sm:px-5 lg:h-full lg:min-h-0 lg:border-b-0 lg:border-r lg:px-6"
          : "flex min-h-[440px] flex-col border-b px-4 py-5 sm:min-h-[470px] sm:px-8 lg:h-full lg:min-h-0 lg:border-b-0 lg:border-r lg:px-10"
      }
      style={{ borderColor: colors.surfaceSubtle }}
    >
      <div
        className={`mx-auto flex w-full items-center justify-between ${
          compact ? "mb-3" : "mb-5 max-w-[820px]"
        }`}
      >
        <h2
          className={
            compact
              ? "text-[17px] font-semibold tracking-normal sm:text-[18px]"
              : "text-[20px] font-semibold tracking-normal"
          }
          style={{ color: colors.textDarkest }}
        >
          {visibleMonth.format("MMMM")}{" "}
          <span className="font-normal" style={{ color: colors.textMuted }}>
            {visibleMonth.format("YYYY")}
          </span>
        </h2>

        <div className="flex items-center gap-2">
          <Button
            type="text"
            size="small"
            disabled={!canGoPreviousMonth}
            icon={<ChevronLeft size={17} />}
            className="!h-8 !w-8 !rounded-full !p-0 disabled:!opacity-35"
            aria-label="Previous month"
            onClick={() => onMonthChange(visibleMonth.subtract(1, "month"))}
          />
          <Button
            type="text"
            size="small"
            disabled={!canGoNextMonth}
            icon={<ChevronRight size={17} />}
            className="!h-8 !w-8 !rounded-full !p-0 disabled:!opacity-35"
            aria-label="Next month"
            onClick={() => onMonthChange(visibleMonth.add(1, "month"))}
          />
        </div>
      </div>

      <div
        className={`mx-auto grid w-full grid-cols-7 text-center text-[12px] font-semibold tracking-[0.1em] sm:text-[13px] ${
          compact ? "mb-2" : "mb-4 max-w-[820px]"
        }`}
        style={{ color: colors.primaryDarkest }}
      >
        {WEEKDAYS.map((day) => (
          <div key={day}>{day}</div>
        ))}
      </div>

      <div
        className={`mx-auto grid min-h-0 w-full flex-1 grid-cols-7 grid-rows-6 gap-x-0 gap-y-0 transition-opacity sm:gap-x-[4px] sm:gap-y-[4px] ${
          compact ? "" : "max-w-[820px]"
        }`}
        style={{ opacity: isDatesLoading ? 0.55 : 1 }}
        aria-busy={isDatesLoading}
      >
        {days.map((date) => {
          const dateKey = date.format("YYYY-MM-DD");
          const isCurrentMonth = date.month() === visibleMonth.month();
          const isSpilloverVisible = isCurrentMonth;
          const isSelected = date.isSame(selectedDate, "day");
          const isAvailable = isDateAvailable(
            date,
            availableDates,
            bookingLink,
          );
          const isToday = date.isSame(dayjs(), "day");

          const backgroundColor = isSelected
            ? brandColor
            : isAvailable
              ? AVAILABLE_DATE_BG
              : "transparent";

          return (
            <div
              key={dateKey}
              className="flex min-w-0 items-center justify-center"
              style={{
                opacity: isSpilloverVisible ? 1 : 0,
                pointerEvents: isSpilloverVisible ? "auto" : "none",
              }}
            >
              <button
                type="button"
                disabled={!isAvailable}
                onClick={() => onDateSelect(date)}
                className={`group relative flex min-w-0 flex-col items-center justify-center text-[14px] font-semibold transition-[background-color,border-color,box-shadow] sm:text-[16px] ${
                  compact ? "rounded-md" : "rounded-lg"
                }`}
                style={{
                  width: compact
                    ? "clamp(36px, 60%, 52px)"
                    : "clamp(66px, calc(76% + 22px), 114px)",
                  aspectRatio: "1 / 1",
                  justifySelf: "center",
                  color: !isAvailable
                    ? colors.surfaceDarkest
                    : isSelected
                      ? colors.white
                      : colors.textDarkest,
                  backgroundColor,
                  border: isSelected
                    ? `1px solid ${brandColor}`
                    : "2px solid transparent",
                  cursor: isAvailable ? "pointer" : "not-allowed",
                  boxShadow: isSelected ? selectedShadow : "none",
                }}
                onMouseEnter={(event) => {
                  if (isAvailable && !isSelected) {
                    event.currentTarget.style.borderColor = "#070A0D";
                  }
                }}
                onMouseLeave={(event) => {
                  if (isAvailable && !isSelected) {
                    event.currentTarget.style.borderColor = "transparent";
                  }
                }}
                aria-label={date.format("dddd, MMMM D, YYYY")}
                aria-pressed={isSelected}
                aria-current={isToday ? "date" : undefined}
              >
                <span>{date.date()}</span>
                {isToday && !isSelected && (
                  <span
                    className="absolute bottom-1.5 h-1.5 w-1.5 rounded-full"
                    style={{ backgroundColor: brandColor }}
                  />
                )}
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
};
